import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Icon } from '../../../../../components/common/icon/Icon';
import { ISubsidyBanner } from '../../../../../interfaces/Banner.interfaces';
import { postMessageToRN } from '../../../../../utils/RNCommunicator/postMessageToRN.util';
import { vibrateWithDuration } from '../../../../../utils/vibration.util';
import { SvgFileNameType } from '../../../../../interfaces/Element.interfaces';
import { ampli } from '../../../../../ampli';

interface SubsidyBannerCardProps {
  banner: ISubsidyBanner;
  city: string;
  state: string;
}

export const SubsidyBannerCard = ({ banner, city, state }: SubsidyBannerCardProps) => {
  const navigate = useNavigate();

  const handleBannerClick = () => {
    vibrateWithDuration(20);
    ampli.pvmClickSubsidyBanner({
      banner_id: banner.id,
      banner_title: banner.title,
      city,
      state,
    });

    if (banner.linkUrl) {
      if (banner.linkUrl.startsWith('http')) {
        if (window.ReactNativeWebView) {
          postMessageToRN({ messageKey: 'link', messageParam: banner.linkUrl });
        } else {
          window.open(banner.linkUrl, '_blank');
        }
      } else {
        navigate(banner.linkUrl);
      }
    }
  };

  const getIconProps = (size: number) => {
    const baseProps = {
      name: banner.icon as SvgFileNameType,
      width: size,
      height: size,
    };

    if (banner.icon === 'Snowflake') {
      return {
        ...baseProps,
        style: { stroke: '#FFFFFF', color: '#FFFFFF' },
      };
    }

    return {
      ...baseProps,
      fill: '#FFFFFF',
    };
  };

  const largeIconProps = getIconProps(80);
  const largeIconStyle = 'style' in largeIconProps ? largeIconProps.style : {};

  return (
    <BannerContainer bgColor={banner.bgColor} onClick={handleBannerClick}>
      <TextSection>
        <Header>
          <Icon {...getIconProps(16)} />
          <CategoryLabel>{banner.categoryLabel}</CategoryLabel>
        </Header>
        <Title>{banner.title}</Title>
        {banner.showDetailBtn && (
          <DetailButton>
            {banner.detailBtnText}
            <Icon name={'ArrowRight'} width={14} height={14} fill={'#FFFFFF'} />
          </DetailButton>
        )}
      </TextSection>
      <LargeIconWrapper>
        <Icon
          {...largeIconProps}
          style={{
            ...largeIconStyle,
            opacity: 0.2,
          }}
        />
      </LargeIconWrapper>
    </BannerContainer>
  );
};

const BannerContainer = styled.div<{ bgColor: string }>`
  position: relative;
  width: 100%;
  padding: 24px 20px;
  border-radius: 12px;
  background-color: ${({ bgColor }) => bgColor};
  overflow: hidden;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const TextSection = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const CategoryLabel = styled.span`
  color: #ffffff;
  font-family: Pretendard;
  font-size: 14px;
  font-weight: 500;
  opacity: 0.9;
`;

const Title = styled.h2`
  color: #ffffff;
  font-family: Pretendard;
  font-size: 20px;
  font-weight: 700;
  line-height: 1.4;
  margin: 0;
  white-space: pre-line;
`;

const DetailButton = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  color: #ffffff;
  font-family: Pretendard;
  font-size: 14px;
  font-weight: 600;
  margin-top: 4px;
`;

const LargeIconWrapper = styled.div`
  position: absolute;
  right: -10px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 0;

  display: flex;
  justify-content: center;
  align-items: center;

  width: 100px;
  height: 100px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 20px;
  backdrop-filter: blur(4px);
`;
